import { useCallback, useEffect, useRef, useState } from 'react';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { EventPageFragment, EventPageType, useRemoveEventPagePhotoMutation, useRemoveRegistryPagePhotoMutation, useSetEventPageAssetMutation } from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import { useDisclosure, useToast } from '@withjoy/joykit';
import { useFilestack } from '@shared/utils/filestack';
import { addRendition } from '@shared/utils/photoRendition';

interface CoupleHeaderArgs {
  eventId: string;
  firebaseId?: string;
  website: string;
  isAdmin: boolean;
  registryPhoto?: string;
  pages?: ReadonlyArray<EventPageFragment>;
}

export const useCoupleHeaderController = ({ eventId, firebaseId, isAdmin, website, registryPhoto, pages }: CoupleHeaderArgs) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const photoRef = useRef<HTMLImageElement>(null);
  const [removeRegistryPagePhoto] = useRemoveRegistryPagePhotoMutation();
  const [removeEventPagePhoto] = useRemoveEventPagePhotoMutation();
  const { currentUser, provideLogout } = useAuth();
  const {
    registryOpenFilePicker: openFilePickerTrack,
    editPageNote: editPageNoteTrack,
    guestReadMore: guestReadMoreTrack,
    registryPagePhotoUpdated: registryPagePhotoUpdatedTrack
  } = useGuestRegistryTelemetry();
  const [showFullPageNote, setShowFullPageNote] = useState<boolean>(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const { toast } = useToast();
  const { getPhotoMenuTranslations } = useRegistryGuestTranslations();
  const [isAtLeastTablet] = useResponsive({ values: { xs: false, sm: false, sm3: true } });
  const [openPageNoteDialogAsAdmin, setOpenPageNoteDialogAsAdmin] = useState<boolean>(isAdmin);
  const [photo, setPhoto] = useState<string | undefined>(registryPhoto);

  useEffect(() => {
    setPhoto(registryPhoto);
  }, [registryPhoto, setPhoto]);

  const { getCoupleHeaderTranslations } = useRegistryGuestTranslations();
  const { readMore, readLess, photoButton, noteButton, addNoteButton, addPhotoButton } = getCoupleHeaderTranslations();

  const [setEventPageAssetMutation] = useSetEventPageAssetMutation();
  const { error, success } = getPhotoMenuTranslations();

  const { open: openFilePicker, getPhotoPreviewUrl } = useFilestack({
    containerId: firebaseId || '',
    onUploadDone: async (res): Promise<void> => {
      registryPagePhotoUpdatedTrack('Update');
      setIsUploadingPhoto(true);
      const { filename, mimetype, size, url } = res.filesUploaded[0];
      const registryPage = pages?.find(page => page.type === EventPageType.registry);
      const id = registryPage?.id ?? '';
      const previewUrl = await getPhotoPreviewUrl({ url });
      const loadImageDimensions = (imageUrl: string): Promise<{ width: number; height: number }> =>
        new Promise(resolve => {
          const image = new Image();

          image.onload = () => {
            const height = image.height;
            const width = image.width;
            resolve({ width, height });
          };

          image.src = imageUrl;
        });

      const dimensions = await loadImageDimensions(previewUrl);

      try {
        await setEventPageAssetMutation({
          variables: {
            id,
            payload: {
              assetId: filename,
              mimeType: mimetype,
              fileSize: size,
              width: dimensions.width,
              height: dimensions.height
            }
          }
        });
        setIsImageLoading(true);
        setPhoto(previewUrl);
        toast(success);
      } catch {
        toast(error);
      } finally {
        setIsUploadingPhoto(false);
      }
    },
    maxFiles: 1
  });

  const expandText = showFullPageNote ? readLess : readMore;

  const { isOpen: isPageNoteDialogOpen, onOpen: openPageNoteDialog, onClose: closePageNoteDialog } = useDisclosure();

  const handleOnUpdatePhoto = useCallback(async () => {
    openFilePickerTrack();
    openFilePicker();
  }, [openFilePickerTrack, openFilePicker]);

  const handleDeletePhoto = useCallback(async () => {
    setIsUploadingPhoto(true);
    try {
      const registryPage = pages?.find(page => page.type === EventPageType.registry);
      const eventPageId = registryPage?.id ?? '';
      await removeEventPagePhoto({ variables: { eventPageId } });
      setIsUploadingPhoto(false);
      registryPagePhotoUpdatedTrack('Delete');
      setPhoto(undefined);
    } catch {
      toast(error);
      setIsUploadingPhoto(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeRegistryPagePhoto, eventId, toast]);

  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false);
  }, [setIsImageLoading]);

  const onEditPageNote = useCallback(() => {
    editPageNoteTrack(eventId);
    setOpenPageNoteDialogAsAdmin(true);
    openPageNoteDialog();
  }, [openPageNoteDialog, setOpenPageNoteDialogAsAdmin, editPageNoteTrack, eventId]);

  const onContinueReading = useCallback(() => {
    guestReadMoreTrack();
    setShowFullPageNote(!showFullPageNote);
  }, [guestReadMoreTrack, setShowFullPageNote, showFullPageNote]);

  const onHandleLogout = () => {
    provideLogout({ eventId });
  };

  const originalPages = pages?.filter(page => !page.disabled && page.type !== EventPageType.app);
  const isNavBarVisible = (originalPages?.length ?? 0) > 1;

  return {
    handleOnUpdatePhoto,
    handleDeletePhoto,
    onEditPageNote,
    onContinueReading,
    isAtLeastTablet,
    showFullPageNote,
    setShowFullPageNote,
    isMobileOrTablet,
    photoRef,
    photo: addRendition({ url: photo, renditionSize: 'small' }),
    currentUser,
    onHandleLogout,
    translations: {
      photoButton,
      noteButton,
      expandText,
      addNoteButton,
      addPhotoButton
    },
    isPageNoteDialogOpen,
    closePageNoteDialog,
    openPageNoteDialogAsAdmin,
    isNavBarVisible,
    isUploadingPhoto: isUploadingPhoto,
    isImageLoading,
    handleImageLoad
  };
};
